/**
 * Configuration for the optional file upload requirements panel.
 *
 * - `maxFileSizeBytes`: Maximum allowed file size in bytes (display + FE validation).
 * - `maxFileCount`: Maximum number of files allowed (display + FE validation).
 *   Default is 10. Override when a consumer needs a higher cap, e.g.
 *   `[requirementsConfig]="{ maxFileCount: 50 }"`.
 * - `additionalItems`: Extra requirement bullets as pre-translated plain strings from the parent.
 */
export interface FileUploadRequirementsConfig {
    maxFileSizeBytes?: number;
    maxFileCount?: number;
    additionalItems?: string[];
}

export const DEFAULT_FILE_UPLOAD_REQUIREMENTS_CONFIG = {
    maxFileSizeBytes: 100 * 1024 * 1024, // 100MiB
    maxFileCount: 10,
} as const;

export type ResolvedFileUploadLimits = Required<
    Pick<FileUploadRequirementsConfig, 'maxFileSizeBytes' | 'maxFileCount'>
>;

/**
 * Merges optional parent overrides with defaults. Uses `??` per field so an
 * explicit `undefined` override cannot replace a default (unlike object spread).
 */
export function resolveFileUploadLimits(
    overrides?: Partial<FileUploadRequirementsConfig>
): ResolvedFileUploadLimits {
    return {
        maxFileSizeBytes:
            overrides?.maxFileSizeBytes ??
            DEFAULT_FILE_UPLOAD_REQUIREMENTS_CONFIG.maxFileSizeBytes,
        maxFileCount:
            overrides?.maxFileCount ??
            DEFAULT_FILE_UPLOAD_REQUIREMENTS_CONFIG.maxFileCount,
    };
}
